import type { CallInfo } from "@rhema/client";
import { Avatar, cn, focusRing, LockIcon, PhoneIcon, UsersIcon, useFocusReturn, VideoIcon } from "@rhema/ui";
import { type ReactNode, useState } from "react";
import { useIsDesktop } from "../app/media";
import { toneFor } from "../data/view";
import { useClientState } from "../session/session";
import { useCalls } from "./CallProvider";
import { callerLine, callTitle, isGroupCall, memberName } from "./people";

function RoundAction({
  label,
  tone,
  onClick,
  disabled,
  children,
}: {
  label: string;
  tone: "accept" | "decline";
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <span className="flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className={cn(
          "flex h-[64px] w-[64px] cursor-pointer items-center justify-center rounded-full border-0 text-white disabled:opacity-60",
          tone === "accept" ? "bg-blue hover:bg-blue-dark" : "bg-scarlet hover:brightness-95",
          focusRing,
        )}
      >
        {children}
      </button>
      <span aria-hidden="true" className="text-[13px] font-semibold text-ink">
        {label}
      </span>
    </span>
  );
}

// A full-screen ring on phones and a card in the corner on desktop. The ringtone and the 45 second
// limit are handled by CallProvider and the call state.
export function IncomingRing({ call }: { call: CallInfo }) {
  const state = useClientState();
  const { accept, decline, notice } = useCalls();
  const desktop = useIsDesktop();
  const [busy, setBusy] = useState(false);
  const ref = useFocusReturn<HTMLElement>(true);
  const group = isGroupCall(state, call.chat_id);
  const title = callTitle(state, call);
  const caller = memberName(state, call.chat_id, call.started_by);
  const video = call.media === "video";

  const onAccept = async () => {
    setBusy(true);
    await accept(call.id);
    setBusy(false);
  };

  return (
    <section
      ref={ref}
      role="alertdialog"
      aria-modal={desktop ? undefined : true}
      aria-label={`${callerLine(state, call)} from ${group ? title : caller}`}
      tabIndex={-1}
      className={cn(
        "z-50 flex flex-col items-center bg-surface-header text-ink outline-none",
        desktop
          ? "fixed top-5 right-5 w-[340px] gap-4 rounded-sheet border border-line p-6 shadow-menu"
          : "fixed inset-0 justify-between px-6 pt-[max(64px,env(safe-area-inset-top))] pb-[max(48px,env(safe-area-inset-bottom))]",
      )}
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <span
          className={cn(
            "rounded-full",
            "shadow-[0_0_0_12px_rgb(36_65_138/0.12),0_0_0_26px_rgb(36_65_138/0.06)]",
          )}
        >
          <Avatar
            name={group ? title : caller}
            tone={toneFor(group ? call.chat_id : (call.started_by ?? call.chat_id))}
            size={desktop ? 76 : 112}
            serif
            shape={group ? "rounded" : "circle"}
            icon={group ? <UsersIcon size={desktop ? 30 : 40} /> : undefined}
          />
        </span>
        <h2 className="m-0 mt-2 font-serif text-[26px] font-medium">{group ? title : caller}</h2>
        <p className="m-0 text-[15px] text-ink-secondary">{callerLine(state, call)}</p>
        <p className="m-0 flex items-center gap-1.5 text-[13px] text-ink-secondary">
          <LockIcon size={12} strokeWidth={2.6} />
          End-to-end encrypted
        </p>
        {notice && (
          <p
            role="alert"
            className="m-0 max-w-[320px] rounded-field bg-scarlet-tint px-3 py-2 text-[13px] text-ink"
          >
            {notice}
          </p>
        )}
      </div>
      <div className={cn("flex w-full items-start justify-around", desktop ? "" : "max-w-[320px]")}>
        <RoundAction label="Decline" tone="decline" onClick={() => void decline(call.id)}>
          <PhoneIcon size={26} strokeWidth={2.2} className="rotate-[135deg]" />
        </RoundAction>
        <RoundAction label="Accept" tone="accept" onClick={() => void onAccept()} disabled={busy}>
          {video ? <VideoIcon size={26} strokeWidth={2.2} /> : <PhoneIcon size={26} strokeWidth={2.2} />}
        </RoundAction>
      </div>
    </section>
  );
}
